import React from "react";
import cn from "clsx";
import { css } from "linaria";
import { useQuery, gql } from "@apollo/client";
import { MenuItem } from "@reach/menu-button";
import addHours from "date-fns/add_hours";
import differenceInHours from "date-fns/difference_in_hours";
import differenceInDays from "date-fns/difference_in_days";
import compareAsc from "date-fns/compare_asc";

import AppLink from "../AppLink";
import FlyerPreview from "./FlyerPreview";

import useFlyerAuthToken from "../../utils/useFlyerAuthToken";
import colors from "../../utils/colorConstants";

const LinkHighlightStyles = css`
  & .highlight-link {
    color: ${colors.gatsby};
    border-color: ${colors.gatsby};
  }
`;

const MAX_DISPLAYED = 1;

const UPCOMING_FLYERS_QUERY = gql`
  query getPages($sortBy: SortBy!, $page: Int!) {
    pages(sortBy: $sortBy, page: $page, pageSize: 100, removed: exclude) {
      edges {
        node {
          id
          status
          event
          previewImageUrl
          productType
          guestsSummary {
            totalGuests
          }
          updatedAt
          artifacts {
            cardFront {
              asset {
                renditions(renditionTypes: ["pngSmall"])
              }
            }
            envelopeBack {
              asset {
                renditions(renditionTypes: ["pngSmall"])
              }
            }
            backdropDesktop {
              asset {
                renditions(renditionTypes: ["jpegSmall"])
              }
            }
            media {
              asset {
                renditions(renditionTypes: ["jpegSmall"])
              }
            }
          }
        }
      }
    }
    receivedPages {
      edges {
        node {
          id
          previewImageUrl
          productType
          receiver {
            id
            guestToken
            status
          }
          event {
            name
            startTime
            endTime
          }
          artifacts {
            cardFront {
              asset {
                renditions(renditionTypes: ["pngSmall"])
              }
            }
            envelopeBack {
              asset {
                renditions(renditionTypes: ["pngSmall"])
              }
            }
            backdropDesktop {
              asset {
                renditions(renditionTypes: ["jpegSmall"])
              }
            }
            media {
              asset {
                renditions(renditionTypes: ["jpegSmall"])
              }
            }
          }
        }
      }
    }
  }
`;

const flattenPages = (data: any) => {
  const sentFlyers = data.pages.edges.map((edge: any) => edge.node.id);

  return data.pages.edges
    .map((edge: any) => ({ ...edge.node, type: "sent" }))
    .concat(
      data.receivedPages.edges
        //filter out flyers where the host invited themselves
        .filter((edge: any) => sentFlyers.indexOf(edge.node.id) === -1)
        .map((edge: any) => ({
          ...edge.node,
          type: "received",
        }))
    );
};

const eventIsRecentOrUpcoming = (event: any) => {
  const endDate =
    (event.endTime && new Date(event.endTime)) ||
    addHours(new Date(event.startTime), 6);
  const timeDiff = differenceInHours(new Date(), endDate);
  return timeDiff <= 48;
};

const filterFeaturedFlyers = (page: any) => {
  const isUpcomingSentFlyer =
    page.event &&
    page.type === "sent" &&
    ["published", "ready", "invite_only"].indexOf(page.status) > -1 &&
    eventIsRecentOrUpcoming(page.event);
  const isUpcomingReceivedFlyer =
    page.event &&
    page.type === "received" &&
    eventIsRecentOrUpcoming(page.event);
  return isUpcomingSentFlyer || isUpcomingReceivedFlyer;
};

const sortByStartDate = (pageA: any, pageB: any) =>
  compareAsc(new Date(pageA.event.startTime), new Date(pageB.event.startTime));

export const getFeaturedFlyers = (data: any) =>
  flattenPages(data).filter(filterFeaturedFlyers).sort(sortByStartDate);

const draftWasUpdatedRecently = (page: any) =>
  differenceInDays(new Date(), new Date(page.updatedAt)) <= 90;

const filterRecentDrafts = (page: any) => {
  return (
    page.type === "sent" &&
    page.status === "draft" &&
    draftWasUpdatedRecently(page)
  );
};

const sortByUpdatedAt = (pageA: any, pageB: any) =>
  compareAsc(new Date(pageA.updatedAt), new Date(pageB.updatedAt));

export const getRecentDrafts = (data: any) =>
  flattenPages(data).filter(filterRecentDrafts).sort(sortByUpdatedAt);

const DivContainer = ({ className, children }: any) => (
  <div className={className}>{children}</div>
);

export default function UpcomingFlyers({
  useMenuItems = false,
}: any) {
  const flyerAuthToken = useFlyerAuthToken();
  const {
    loading,
    error: gqlError,
    data,
  } = useQuery(UPCOMING_FLYERS_QUERY, {
    fetchPolicy: "cache-and-network",
    variables: {
      sortBy: "updatedAt",
      page: 1,
    },
    context: {
      headers: {
        Authorization: `Bearer ${flyerAuthToken}`,
      },
    },
    skip: !flyerAuthToken,
  });

  const loadingOrEmpty =
    gqlError ||
    loading ||
    !data ||
    (!data.pages.edges.length && !data.receivedPages.edges);

  let listTitle = "";
  let flyersToDisplay = [];
  let totalFeatured = 0;
  let manageHref = "/manage";

  if (!loadingOrEmpty) {
    listTitle = "Recent Activity";
    flyersToDisplay = getFeaturedFlyers(data);
    totalFeatured = flyersToDisplay.length;
    manageHref = "/manage";

    if (!flyersToDisplay.length) {
      listTitle = "Recent Flyer draft";
      flyersToDisplay = getRecentDrafts(data);
      totalFeatured = flyersToDisplay.length;
      manageHref = `${manageHref}/draft`;
    }
  }

  if (!flyersToDisplay.length) {
    return null;
  }

  const Container = useMenuItems ? MenuItem : DivContainer;
  const FirstItemContainer =
    totalFeatured > MAX_DISPLAYED ? Container : DivContainer;

  return (
    <>
      <FirstItemContainer
        className={cn("px-3 pt-2", LinkHighlightStyles)}
        onSelect={() => {}}
      >
        <div className="flex paragraph-medium">
          <div className="flex-1">{listTitle}</div>
          {totalFeatured > MAX_DISPLAYED && (
            <AppLink
              className="flex highlight-link"
              href={manageHref}
              tracking={{
                id: "upcomingFlyer",
                block_name: "account_drawer",
                block_row: "account_drawer",
              }}
            >
              View all ({totalFeatured})
            </AppLink>
          )}
        </div>
      </FirstItemContainer>
      {flyersToDisplay.slice(0, MAX_DISPLAYED).map((page: any) => (
        <Container
          key={page.id}
          className={cn("px-3 py-2", LinkHighlightStyles)}
          onSelect={() => {}}
        >
          <FlyerPreview page={page} />
        </Container>
      ))}
    </>
  );
}
